import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import StatementValidationReport from "./StatementValidationReport";

/**
 * Bank Statement Certification Panel.
 *
 * Lives on each ImportBatch card (bank imports only). Lets the Controller:
 *   1. Upload a statement PDF
 *   2. Trigger the pipeline (E1 extract → E2 validate → E3 certify)
 *   3. See the validation report
 *   4. Optionally override a failed validation with a ≥25-char reason
 *
 * All engines run on the backend. The panel just orchestrates HTTP.
 */

export default function BankStatementCertificationPanel({ batchId, onChanged, showToast }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [running, setRunning] = useState(false);
  const [showOverride, setShowOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const fileInputRef = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.batchCertification(batchId);
      setReport(r);
    } catch (e) {
      showToast?.("Failed to load bank certification: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [batchId, showToast]);

  useEffect(() => { load(); }, [load]);

  async function onUpload(file) {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      showToast?.("Only PDF files are accepted.");
      return;
    }
    try {
      setUploading(true);
      const doc = await api.uploadStatementPdf(batchId, file);
      showToast?.("PDF uploaded. Running extraction…");
      setUploading(false);
      setRunning(true);
      // The pipeline runs asynchronously on the backend (returns 202
      // immediately). We poll the report endpoint until the document
      // reaches a terminal status.
      await api.runStatementPipeline(doc.id);
      await pollUntilDone(doc.id);
    } catch (e) {
      showToast?.("Upload/pipeline failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
      setRunning(false);
    }
  }

  async function rerunPipeline() {
    if (!report?.document?.id) return;
    try {
      setRunning(true);
      await api.runStatementPipeline(report.document.id);
      await pollUntilDone(report.document.id);
    } catch (e) {
      showToast?.("Pipeline failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setRunning(false);
    }
  }

  async function pollUntilDone(docId) {
    // Poll every 4 seconds up to ~5 minutes.
    const TERMINAL = new Set([
      "certified_automatic",
      "certified_override",
      "validation_failed",
      "extraction_failed",
    ]);
    const started = Date.now();
    const MAX_MS = 5 * 60 * 1000;
    for (;;) {
      await new Promise(r => setTimeout(r, 4000));
      try {
        const doc = await api.statementReport(docId);
        setReport(doc);
        const status = doc?.document?.latest_status;
        if (status && TERMINAL.has(status)) {
          showToast?.(
            status === "certified_automatic" ? "Bank Automatically Certified" :
            status === "certified_override"  ? "Certified by controller override" :
            status === "extraction_failed"   ? "Extraction failed — see report" :
                                               "Validation failed — review exceptions"
          );
          onChanged?.();
          return;
        }
      } catch (e) {
        // Transient error while polling — keep going unless we've timed out.
      }
      if (Date.now() - started > MAX_MS) {
        showToast?.("Pipeline is taking longer than expected. Refresh to check status.");
        return;
      }
    }
  }

  async function submitOverride() {
    if (!report?.validation?.id) return;
    if (overrideReason.trim().length < 25) {
      showToast?.("Override reason must be at least 25 characters.");
      return;
    }
    try {
      await api.overrideStatementValidation(report.validation.id, overrideReason.trim());
      showToast?.("Certified with controller override — audit trail written.");
      setShowOverride(false);
      setOverrideReason("");
      await load();
      onChanged?.();
    } catch (e) {
      showToast?.("Override failed: " + (e?.response?.data?.detail || e.message));
    }
  }

  const document = report?.document;
  const validation = report?.validation;
  const certification = report?.certification;
  const status = document?.latest_status || null;
  const canOverride = validation && !validation.passed
    && certification?.status !== "certified_automatic"
    && certification?.status !== "certified_override";

  return (
    <div className="bank-cert-panel" data-testid={`bank-cert-panel-${batchId}`}>
      <div className="bank-cert-panel-head">
        <div>
          <div className="heading h3" style={{ margin: 0 }}>Bank Statement Certification</div>
          <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
            Upload the official bank statement PDF. The pipeline extracts, validates,
            and certifies the imported bank feed against the statement.
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {document && (
            <a
              href={api.statementPdfUrl(document.id)}
              target="_blank" rel="noopener noreferrer"
              className="btn ghost btn-sm"
              data-testid="bank-cert-view-pdf">View PDF</a>
          )}
          <button
            className="btn primary btn-sm"
            disabled={uploading || running}
            onClick={() => fileInputRef.current?.click()}
            data-testid="bank-cert-upload-btn">
            {document ? "Upload New Statement" : "Attach Statement PDF"}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            style={{ display: "none" }}
            onChange={e => { onUpload(e.target.files?.[0]); e.target.value = ""; }}
          />
          {document && !running && !uploading && (
            <button
              className="btn btn-sm"
              onClick={rerunPipeline}
              data-testid="bank-cert-rerun-btn"
              title="Re-run extraction and validation on the currently attached PDF">
              Re-run Pipeline
            </button>
          )}
        </div>
      </div>

      {loading && <div className="muted" style={{ fontSize: 13 }}>Loading…</div>}
      {(uploading || running) && (
        <div className="cert-progress" data-testid="bank-cert-progress">
          {uploading ? "Uploading PDF…" : "Running pipeline (extract → validate → certify)…"}
          <div className="cert-progress-note muted" style={{ fontSize: 11 }}>
            Extraction is LLM-powered and typically takes 10–40 seconds for a 15-page statement.
          </div>
        </div>
      )}

      {!loading && !document && !uploading && (
        <div className="empty" style={{ marginTop: 8 }}>
          No bank statement attached to this import yet.
        </div>
      )}

      {document && <StatementValidationReport report={report} />}

      {canOverride && !showOverride && (
        <div style={{ marginTop: 12 }}>
          <button
            className="btn ghost btn-sm"
            onClick={() => setShowOverride(true)}
            data-testid="bank-cert-override-open"
            title="Only use if you can independently confirm the discrepancy is safe to accept (e.g. bank called to confirm)">
            Certify with Controller Override…
          </button>
        </div>
      )}

      {showOverride && (
        <div className="cert-override-panel" data-testid="bank-cert-override-panel">
          <div className="heading h3" style={{ marginBottom: 4 }}>Controller Override</div>
          <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
            This will produce a "Certified by Controller Override" record — <strong>not</strong> the
            same as automatic certification. A reason of at least 25 characters is required
            and written to the audit log.
          </div>
          <textarea
            value={overrideReason}
            onChange={e => setOverrideReason(e.target.value)}
            placeholder="Example: BMO branch confirmed the missing deposit by phone on Aug 8, 2026 — ref #12345."
            rows={4}
            data-testid="bank-cert-override-reason"
            style={{ width: "100%", padding: 8, border: "1px solid var(--line-strong)", borderRadius: 6, fontSize: 13 }}
          />
          <div style={{ marginTop: 6, fontSize: 11 }} className="muted">
            {overrideReason.trim().length} / 25 characters minimum
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
            <button
              className="btn ghost btn-sm"
              onClick={() => { setShowOverride(false); setOverrideReason(""); }}
              data-testid="bank-cert-override-cancel">Cancel</button>
            <button
              className="btn primary btn-sm"
              disabled={overrideReason.trim().length < 25}
              onClick={submitOverride}
              data-testid="bank-cert-override-submit">
              Certify with Override
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
